import { type TablePaginationConfig } from 'antd';
import { type ColumnsType, type ColumnType } from 'antd/es/table';
import { type SorterResult } from 'antd/es/table/interface';
import { useCallback, useMemo } from 'react';
import { type NotificationListParams } from '../search/NotificationListParams.ts';
import {
  DEFAULT_SORT_DIRECTION,
  DEFAULT_SORT_FIELD,
  getSortParams,
} from './getSortParams.ts';
import { type NotificationRecord } from './notificationColumnsBase.tsx';

/**
 * Hook per gestire paginazione e ordinamento server-side della tabella notifiche.
 * Segue il pattern di `useDossierPaginationChange`.
 */
export function useNotificationPaginationChange(
  query: NotificationListParams,
  setQuery: (query: NotificationListParams) => void,
) {
  const onChange = useCallback(
    (
      pagination: TablePaginationConfig,
      _filters: any,
      sorter:
        | SorterResult<NotificationRecord>
        | SorterResult<NotificationRecord>[],
    ) => {
      const sortResult = Array.isArray(sorter) ? sorter[0] : sorter;

      const pageSize = pagination.pageSize ?? (Number(query.maxResults) || 20);
      const current = pagination.current ?? 1;

      setQuery({
        ...query,
        ...getSortParams(sortResult, query),
        firstResult: (current - 1) * pageSize,
        maxResults: pageSize,
      });
    },
    [query, setQuery],
  );

  return onChange;
}

/**
 * Imposta il `sortOrder` sulle colonne in base ai parametri di ordinamento correnti.
 * Se non c'è un ordinamento attivo, mostra il default (DATA_NOTIFICA DESC).
 */
export function useNotificationSortedColumns(
  columns: ColumnsType<NotificationRecord>,
  query: NotificationListParams,
) {
  const sortField = query.sortField ?? DEFAULT_SORT_FIELD;
  const sortDirection = query.sortDirection ?? DEFAULT_SORT_DIRECTION;

  return useMemo(
    () =>
      columns.map((col: ColumnType<NotificationRecord>) => {
        if ((col.sortDataIndex ?? col.dataIndex) === sortField) {
          return {
            ...col,
            sortOrder:
              sortDirection === 'ASC'
                ? ('ascend' as const)
                : ('descend' as const),
          };
        }
        return { ...col, sortOrder: null };
      }),
    [columns, sortField, sortDirection],
  );
}
